<script setup lang="ts">
import { refreshToken } from "@cms/core/refresh";
import { provideLoader } from "@cms/features/Loader/useLoader";
import MobileTabBar from "@cms/features/Navbar/MobileTabBar.vue";
import NavbarContainer from "@cms/features/Navbar/NavbarContainer.vue";
import * as Sentry from "@sentry/vue";
import { useBootLoader, useFirstRouteRendered } from "@verbleif/lib/src/components/BrandLoader/useBootLoader";
import DialogHost from "@verbleif/lib/src/components/Dialog/DialogHost.vue";
import VSidebarMobileBar from "@verbleif/lib/src/components/Sidebar/VSidebarMobileBar.vue";
import { updateLocale } from "@verbleif/lib/src/translations/localeState";
import { sharedDebugSections } from "@verbleif/shared/src/components/DebugTools/sections/sharedDebugSections";
import { useDebugConfigAdapter } from "@verbleif/shared/src/components/DebugTools/useDebugConfigAdapter";
import { useDebugTools } from "@verbleif/shared/src/components/DebugTools/useDebugTools";
import AppToaster from "@verbleif/shared/src/components/Sonner/AppToaster.vue";
import { useAppUpdate } from "@verbleif/shared/src/composables/AppUpdate/useAppUpdate";
import { useAppUpdateDialog } from "@verbleif/shared/src/composables/AppUpdate/useAppUpdateDialog";
import { isOAuthCallbackWithCode } from "@verbleif/shared/src/composables/AuthStore/oauthCallback";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { isAdmin, usePermsWatch } from "@verbleif/shared/src/composables/Permissions/SharedPermissions";
import { useLocaleQueryReset } from "@verbleif/shared/src/composables/Query/useLocaleQueryReset";
import { useResourceCatalog } from "@verbleif/shared/src/composables/Query/useResourceCatalog";
import { useTheme } from "@verbleif/shared/src/composables/Theme/Theme";
import { setYupLocales } from "@verbleif/shared/src/plugins/yupLocales";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const loaderProvider = provideLoader();
const { t } = useI18n();

useResourceCatalog({
  route,
  include: "labels",
});

const { init: initAppUpdate } = useAppUpdate();
initAppUpdate(router, () => import("virtual:pwa-register/vue"));
useAppUpdateDialog();
useDebugTools({
  app: "cms",
  config: useDebugConfigAdapter(),
  admin: isAdmin,
  sections: sharedDebugSections,
});

useLocaleQueryReset();

watch(() => authStore.user.value?.locale, (loc) => {
  if (route.meta.skipAuthChecks !== true) {
    updateLocale(loc?.toLowerCase() ?? "nl");
  }
  setYupLocales(t);
}, { immediate: true });

const hideNavbar = computed(
  () => route.path === "/" || route.meta.hideNavbar === true,
);
const isPublicRoute = computed(
  () => route.path === "/" || route.meta.public === true,
);
const globalLoading = ref(true);

watch(
  authStore.user,
  (newUser, oldUser) => {
    if (!newUser) {
      return;
    }

    if (oldUser?.id === newUser?.id) {
      return;
    }

    Sentry.setUser(authStore.user.value);
  },
);

if (!authStore.hasToken.value && !authStore.isRemembered.value) {
  authStore.setUser(null); // Reset it to be sure.
  globalLoading.value = false;
  loaderProvider.hide();
}

usePermsWatch();

// The OAuth callback exchanges its own code for a fresh pair. Refreshing a remembered token first would run a
// token the callback is about to replace, and a rejected one would send the user away before the code is used.
if (authStore.hasToken.value || authStore.isRemembered.value) {
  if (isOAuthCallbackWithCode()) {
    globalLoading.value = false;
    loaderProvider.hide();
  } else {
    refreshToken()
      .finally(() => {
        globalLoading.value = false;
        loaderProvider.hide();
      });
  }
}

const loading = computed(() => loaderProvider.loading.value);
const firstRouteRendered = useFirstRouteRendered(router);
useBootLoader(() => loading.value || globalLoading.value || !firstRouteRendered.value);
useTheme();
</script>

<template>
  <AppToaster />
  <DialogHost />
  <template v-if="!globalLoading">
    <template v-if="!isPublicRoute && !hideNavbar">
      <NavbarContainer :class="{ 'is-visible': !hideNavbar }" class="navbar" />
      <MobileTabBar />
      <!-- GLOBAL COMPONENTS THAT RELY ON A USER STATE SHOULD BE PLACED HERE -->
    </template>
    <div class="app-container" :class="{ 'is-login-route': !hideNavbar }">
      <VSidebarMobileBar />
      <router-view v-slot="{ Component }">
        <transition mode="out-in" name="fade">
          <component :is="Component" />
        </transition>
      </router-view>
    </div>
  </template>
</template>

<style src="./tailwind.css" />

<style lang="scss" src="./main.scss" />
